import { useLocale } from "core/hooks/useLocale";
import { useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";

/**
 * A custom hook that synchronizes form validation errors when the application language changes.
 * This ensures that error messages are properly translated and re-triggered when switching between locales.
 *
 * @param formMethods - The form methods object from react-hook-form containing form state and validation methods
 *
 * @example
 * ```tsx
 * const formMethods = useForm();
 * useSynchronizeErrorLanguage(formMethods);
 * ```
 */
export function useSynchronizeErrorLanguage(formMethods: UseFormReturn<any>) {
  const locale = useLocale();
  const previousLocaleRef = useRef(locale);
  const { errors } = formMethods.formState;
  const formHasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (previousLocaleRef.current !== locale && formHasErrors) {
      previousLocaleRef.current = locale;
      formMethods.trigger();
    }
  }, [locale, formMethods, formHasErrors]);
}
